/**
 * Skirmish bot. Runs INSIDE the sim (deterministically, once per second per
 * bot player), so every lockstep peer computes identical bot behavior.
 *
 * It plays a straightforward FinOps game: build token capacity, keep unit
 * economics positive, research cost optimizations, expand to markets, and
 * raid the rival's infrastructure once it can afford to.
 */

import { BUILDINGS, CYBER, ECONOMY, FACTIONS, TECHS, UNITS } from './data';
import {
  dist,
  entityCenter,
  isBlocked,
  playerBuildings,
  playerUnits,
  activeResearch,
  activePhaseWork,
  activeFineTune,
  activeTrainingRun,
  frontierRunCost,
  serveDataCost,
  isExplored,
  isServeUnit,
} from './sim';
import type {
  Building,
  BuildingType,
  Command,
  DataNode,
  GameState,
  Market,
  PlayerIdx,
  TechId,
  Unit,
  UnitType,
  Vec2,
} from './types';

export function findBuildSpot(state: GameState, near: Vec2, size: number): Vec2 | null {
  // spiral out from `near` looking for a clear footprint (+1 tile margin)
  for (let r = 2; r < 14; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = Math.floor(near.x) + dx;
        const y = Math.floor(near.y) + dy;
        let clear = true;
        for (let by = -1; by <= size && clear; by++) {
          for (let bx = -1; bx <= size && clear; bx++) {
            if (isBlocked(state, x + bx, y + by)) clear = false;
          }
        }
        if (!clear) continue;
        // avoid stamping on nodes/markets/units
        for (const e of state.entities) {
          if (e.kind === 'building') continue;
          if (
            e.pos.x >= x - 1 &&
            e.pos.x < x + size + 1 &&
            e.pos.y >= y - 1 &&
            e.pos.y < y + size + 1
          ) {
            clear = false;
            break;
          }
        }
        if (clear) return { x, y };
      }
    }
  }
  return null;
}

const TECH_ORDER: TechId[] = [
  'taggingPolicy',
  'promptCaching',
  'anomalyDetection',
  'commitmentDiscount1',
  'modelRouting',
  'autoscaling',
  'batchInference',
  'unitEconomics',
  'commitmentDiscount2',
  'focusAdoption',
];

/** Behavioral tuning per difficulty — no resource cheats, just sharper play. */
interface BotTuning {
  engineers: number;
  /** wanted inference agents by phase */
  agents: [number, number, number];
  /** wanted combat units by phase (phase 1, phase 2+) */
  combat: [number, number];
  /** how many techs deep the bot bothers to research */
  techDepth: number;
  /** multiplier on budget thresholds before advancing phase */
  phaseCaution: number;
  /** idle combat units needed before raiding (phase 1, phase 2+) */
  raidSize: [number, number];
}

const TUNING: Record<'easy' | 'normal' | 'hard', BotTuning> = {
  easy: {
    engineers: 4,
    agents: [3, 4, 5],
    combat: [2, 3],
    techDepth: 3,
    phaseCaution: 1.6,
    raidSize: [99, 6], // easy bots only ever raid in Run phase, en masse
  },
  normal: {
    engineers: 5,
    agents: [4, 7, 9],
    combat: [3, 6],
    techDepth: TECH_ORDER.length,
    phaseCaution: 1.0,
    raidSize: [5, 4],
  },
  hard: {
    engineers: 6,
    agents: [5, 9, 12],
    combat: [4, 8],
    techDepth: TECH_ORDER.length,
    phaseCaution: 0.8,
    raidSize: [3, 4],
  },
};

export function botCommands(state: GameState, me: PlayerIdx): Command[] {
  const p = state.players[me];
  const tune = TUNING[p.difficulty ?? 'normal'];
  const cmds: Command[] = [];
  const myUnits = playerUnits(state, me);
  const myBuildings = playerBuildings(state, me);
  const hq = myBuildings.find((b) => b.type === 'hq');
  if (!hq) return cmds;
  const hqC = { x: hq.pos.x + 1.5, y: hq.pos.y + 1.5 };

  const engineers = myUnits.filter((u) => u.type === 'engineer');
  const agents = myUnits.filter((u) => isServeUnit(u.type));
  // Every combat-capable unit counts toward the army cap — including the
  // faction Run units (forkNode, spotInstance, superCluster). Enumerating
  // types here once let forkNode swarms grow unbounded: they never counted,
  // so `combat.length < wantCombat` never became false.
  const combat = myUnits.filter(
    (u) => u.type !== 'engineer' && u.type !== 'scoutCrawler' && UNITS[u.type].attack,
  );
  const has = (t: BuildingType) => myBuildings.some((b) => b.type === t);
  const operational = (t: BuildingType) =>
    myBuildings.some((b) => b.type === t && b.buildTicks === 0);
  const count = (t: BuildingType) => myBuildings.filter((b) => b.type === t).length;

  // --- immediate anomaly hygiene (no FinOps center yet -> manual kill) ---
  if (!operational('finopsCenter')) {
    for (const u of myUnits) {
      if (u.anomalyTicks > 0) cmds.push({ type: 'fixAnomaly', unitId: u.id });
    }
  }

  // --- keep engineer count up (RAG doctrine needs a bigger data pipeline) ---
  const dataDemand = agents.length * serveDataCost(p) * 0.67; // data/sec while serving
  // Run-phase fleets drain data faster than 8 engineers can gather (RAG
  // especially) — without a bigger pipeline the bot serves stale forever
  // and can never bank data for fine-tunes or frontier training runs.
  const wantEngineers = Math.min(
    p.phase >= 2 ? 12 : 8,
    Math.max(tune.engineers, Math.ceil(dataDemand) + 2),
  );
  if (engineers.length < wantEngineers && hq.queue.length === 0 && p.budget > 200) {
    cmds.push({ type: 'train', buildingId: hq.id, unit: 'engineer' });
  }

  // --- rescue orphaned construction sites (builder died / got pulled) ---
  const orphaned = myBuildings.filter(
    (b) =>
      b.buildTicks > 0 &&
      !myUnits.some((u) => u.order.kind === 'build' && u.order.targetId === b.id),
  );
  for (const site of orphaned) {
    const rescuer = engineers
      .filter((u) => u.order.kind !== 'build')
      .sort((a, b) => dist(a.pos, site.pos) - dist(b.pos, site.pos))[0];
    if (rescuer) {
      cmds.push({ type: 'repair', unitIds: [rescuer.id], buildingId: site.id });
    }
  }

  // --- put idle engineers to work gathering (only nodes we've SEEN) ---
  const nodes = state.entities.filter(
    (e): e is DataNode =>
      e.kind === 'dataNode' && e.remaining > 0 && isExplored(state, me, e.pos.x, e.pos.y),
  );
  for (const eng of engineers) {
    if (eng.order.kind === 'idle' && nodes.length > 0) {
      const nearest = nodes.sort((a, b) => dist(a.pos, eng.pos) - dist(b.pos, eng.pos))[0];
      cmds.push({ type: 'gather', unitIds: [eng.id], nodeId: nearest.id });
    }
  }

  // --- build order ---
  const builder =
    engineers.find((u) => u.order.kind === 'idle') ??
    engineers.find((u) => u.order.kind === 'gather' || u.order.kind === 'returnData');
  const tryBuild = (t: BuildingType, reserve = 0): boolean => {
    if (!builder) return false;
    const bs = BUILDINGS[t];
    if (p.phase < bs.phase) return false;
    if (p.budget < bs.costBudget + reserve || p.data < bs.costData) return false;
    const spot = findBuildSpot(state, hqC, bs.size);
    if (!spot) return false;
    cmds.push({ type: 'build', unitId: builder.id, building: t, x: spot.x, y: spot.y });
    return true;
  };

  const tokenless = Boolean(FACTIONS[p.faction].tokenless);
  const wantClusters = tokenless ? 0 : p.phase === 0 ? 1 : p.phase === 1 ? 2 : 3;
  let queuedBuild = false;
  // power first: brownouts throttle everything, so a substation outranks
  // whatever else we wanted. Count under-construction substations as pending
  // capacity, or we'll panic-order five more while the first one builds.
  const subPower = FACTIONS[p.faction].substationPowerOverride ?? BUILDINGS.substation.powerCapAdd;
  const pendingPower = myBuildings
    .filter((b) => b.type === 'substation' && b.buildTicks > 0)
    .length * subPower;
  if (p.powerCap > 0 && p.powerDraw > p.powerCap + pendingPower - 2) {
    queuedBuild = tryBuild('substation');
  }
  if (!queuedBuild) {
    if (!tokenless && !has('gpuCluster')) queuedBuild = tryBuild('gpuCluster');
    else if (!has('inferenceEndpoint')) queuedBuild = tryBuild('inferenceEndpoint');
    else if (!has('dataWarehouse')) queuedBuild = tryBuild('dataWarehouse', 100);
    else if (!has('finopsCenter')) queuedBuild = tryBuild('finopsCenter', 150);
    else if (!tokenless && count('gpuCluster') < wantClusters && p.tokens < p.tokenCap * 0.35)
      queuedBuild = tryBuild('gpuCluster', 200);
    else if (p.phase >= 1 && !has('cacheLayer')) queuedBuild = tryBuild('cacheLayer', 250);
    else if (p.phase >= 1 && count('firewall') < 2) queuedBuild = tryBuild('firewall', 300);
    // Blue team: a SOC halves every incoming cyber attack — worth it once the
    // economy has legs.
    else if (p.phase >= 1 && !has('socCenter')) queuedBuild = tryBuild('socCenter', 300);
    // Red team: an Adversarial Lab lets hard/normal bots harass rivals. Easy
    // bots stay peaceful builders.
    else if (p.phase >= 1 && p.difficulty !== 'easy' && !has('adversarialLab'))
      queuedBuild = tryBuild('adversarialLab', 350);
    else if (p.phase >= 2 && !has('trainingCluster')) queuedBuild = tryBuild('trainingCluster', 300);
    else if (p.phase >= 2 && count('dataWarehouse') < 2 && p.data > p.dataCap * 0.9)
      queuedBuild = tryBuild('dataWarehouse', 200);
    else if (
      p.phase >= 2 &&
      // a mined-out map, OR cash piling up while the data pool starves —
      // late-game serving fleets drain data faster than gathering refills
      // it, and synthetic data is the designed money-into-data sink (it's
      // also what feeds fine-tunes and frontier training runs)
      ((!has('syntheticData') && nodes.reduce((s, n) => s + n.remaining, 0) < 900) ||
        (count('syntheticData') < 3 && p.budget > 2500 && p.data < 80))
    )
      queuedBuild = tryBuild('syntheticData', 250);
  }

  // --- doctrine: openweights/serverless lean RAG (data-rich, margin-now);
  // hyperscale leans fine-tune (capital compounding) ---
  if (p.doctrine === null && p.phase >= 1 && operational('finopsCenter')) {
    cmds.push({
      type: 'chooseDoctrine',
      doctrine: p.faction === 'hyperscale' ? 'finetune' : 'rag',
    });
  }

  // --- fine-tune jobs once the economy can absorb the hit ---
  if (
    p.doctrine === 'finetune' &&
    !activeFineTune(state, me) &&
    p.revenuePerSec > p.burnPerSec &&
    p.data > 160 &&
    p.tokens > 350
  ) {
    const cluster = myBuildings.find((b) => b.type === 'trainingCluster' && b.buildTicks === 0);
    if (cluster) cmds.push({ type: 'fineTune', buildingId: cluster.id });
  }

  // --- frontier training runs (Economy 3.0): push the frontier while the
  // economy is healthy; ALWAYS chase it when behind (fast-follow is
  // discounted, and staying behind bleeds revenue on every serve) ---
  if (!activeTrainingRun(state, me)) {
    const cluster = myBuildings.find((b) => b.type === 'trainingCluster' && b.buildTicks === 0);
    if (cluster) {
      const runCost = frontierRunCost(state, p);
      const behind = p.modelGen < state.frontierGen;
      // easy bots never race for first place — they only catch up
      const wantsRun = behind || (p.difficulty !== 'easy' && p.revenuePerSec > p.burnPerSec);
      if (
        wantsRun &&
        p.budget > runCost.budget + 400 &&
        p.data >= runCost.data &&
        p.tokens >= runCost.tokens
      ) {
        cmds.push({ type: 'trainingRun', buildingId: cluster.id });
      }
    }
  }

  // --- research (parallel tracks: one job per lab type) ---
  for (const lab of ['finopsCenter', 'inferenceEndpoint'] as const) {
    if (!operational(lab) || activeResearch(state, me, lab)) continue;
    for (const t of TECH_ORDER.slice(0, tune.techDepth)) {
      const ts = TECHS[t];
      if (ts.lab !== lab) continue;
      if (ts.notFaction && p.faction === ts.notFaction) continue;
      if (p.techs.includes(t)) continue;
      if (p.phase < ts.phase) continue;
      if (ts.requires && !p.techs.includes(ts.requires)) continue;
      if (p.budget < ts.costBudget + 250 || p.data < ts.costData) continue;
      cmds.push({ type: 'research', tech: t });
      break;
    }
  }

  // --- train inference agents (the revenue engine) ---
  const endpoint = myBuildings.find((b) => b.type === 'inferenceEndpoint' && b.buildTicks === 0);
  const wantAgents = tune.agents[p.phase];
  if (
    endpoint &&
    agents.length < wantAgents &&
    endpoint.queue.length === 0 &&
    p.budget > UNITS.inferenceAgent.costBudget + 150 &&
    // bootstrap: the first agents must be trained even at negative margin,
    // or there is never any revenue to fix the margin with
    (agents.length < 2 || p.revenuePerSec - p.burnPerSec > -6)
  ) {
    const unit =
      p.faction === 'openweights' && agents.length % 2 === 1
        ? 'distilledAgent'
        : 'inferenceAgent';
    cmds.push({ type: 'train', buildingId: endpoint.id, unit });
  }

  // serverless demand spikes: burst workers only while there's actually
  // free market demand to serve (bursts beyond that just saturate payouts)
  if (
    tokenless &&
    endpoint &&
    endpoint.queue.length === 0 &&
    p.budget > 900 &&
    p.revenuePerSec > p.burnPerSec
  ) {
    const bursts = myUnits.filter((u) => u.type === 'burstWorker').length;
    const knownMarkets = state.entities.filter(
      (e): e is Market => e.kind === 'market' && isExplored(state, me, e.pos.x, e.pos.y),
    );
    const freeDemand = knownMarkets.reduce((sum, m) => {
      const servers = state.entities.filter(
        (e) => e.kind === 'unit' && (e as Unit).order.kind === 'serve' && (e as Unit).order.targetId === m.id,
      ).length;
      return sum + Math.max(0, m.demand - servers);
    }, 0);
    if (bursts < Math.min(4, freeDemand)) {
      cmds.push({ type: 'train', buildingId: endpoint.id, unit: 'burstWorker' });
    }
  }

  // --- assign idle agents to markets with free demand (fog-honest) ---
  const markets = state.entities.filter(
    (e): e is Market => e.kind === 'market' && isExplored(state, me, e.pos.x, e.pos.y),
  );

  // --- scouting: markets migrate, so idle scouts sweep unexplored waypoints
  const scouts = myUnits.filter((u) => u.type === 'scoutCrawler');
  for (const sc of scouts) {
    if (sc.order.kind !== 'idle') continue;
    const W = state.map.width;
    const H = state.map.height;
    const waypoints: Vec2[] = [
      { x: W / 2, y: H / 2 },
      { x: W / 2, y: 8 }, { x: W / 2, y: H - 8 },
      { x: 8, y: H / 2 }, { x: W - 8, y: H / 2 },
      { x: W / 4, y: (3 * H) / 4 }, { x: (3 * W) / 4, y: H / 4 },
      { x: W / 4, y: H / 4 }, { x: (3 * W) / 4, y: (3 * H) / 4 },
    ];
    const unseen = waypoints.filter((p2) => !isExplored(state, me, p2.x, p2.y));
    const pick = (unseen.length > 0 ? unseen : waypoints)[
      Math.floor(state.tick / (20 * 8)) % (unseen.length > 0 ? unseen.length : waypoints.length)
    ];
    cmds.push({ type: 'move', unitIds: [sc.id], x: pick.x, y: pick.y });
  }
  for (const a of agents) {
    if (a.order.kind !== 'idle') continue;
    const ranked = markets
      .map((m) => {
        const servers = state.entities.filter(
          (e) =>
            e.kind === 'unit' &&
            (e as Unit).order.kind === 'serve' &&
            (e as Unit).order.targetId === m.id,
        ).length;
        return { m, free: m.demand - servers, d: dist(a.pos, m.pos) };
      })
      .filter((r) => r.free > 0)
      .sort((x, y) => x.d - y.d);
    if (ranked.length > 0) {
      cmds.push({ type: 'serve', unitIds: [a.id], marketId: ranked[0].m.id });
    }
  }

  // --- phase advancement ---
  if (!activePhaseWork(state, me)) {
    const adv =
      (p.phase === 0 && agents.length >= 3 && p.budget > 900 * tune.phaseCaution && p.data >= 40) ||
      (p.phase === 1 && p.budget > 1800 * tune.phaseCaution && p.data >= 100 && p.revenuePerSec > p.burnPerSec);
    if (adv) cmds.push({ type: 'advancePhase' });
  }

  // --- military ---
  if (p.phase >= 1 && endpoint && endpoint.queue.length === 0 && !queuedBuild) {
    const wantCombat = tune.combat[p.phase >= 2 ? 1 : 0];
    // At Run phase the Training Cluster (fine-tunes + frontier runs) outranks
    // a bigger army: keep a savings floor until it's up, or high-burn factions
    // never bank enough to enter the frontier race at all.
    const militaryFloor = p.phase >= 2 && !has('trainingCluster') ? 1300 : 500;
    if (combat.length < wantCombat && p.budget > militaryFloor && p.revenuePerSec > p.burnPerSec * 0.8) {
      // Siege (loadTester) is Run-only now — only fold it into the mix once
      // the phase actually unlocks it, or the train just bounces.
      const siege: UnitType[] = p.phase >= UNITS.loadTester.phase ? ['loadTester'] : [];
      // faction Run signature units that train at the endpoint (hyperscale's
      // Supercluster comes from the Training Cluster instead — see below)
      const special: UnitType[] =
        p.phase < 2
          ? []
          : p.faction === 'serverless'
            ? ['spotInstance']
            : p.faction === 'openweights'
              ? ['forkNode']
              : [];
      const base: UnitType[] =
        p.faction === 'hyperscale'
          ? ['reservedInstance', 'redTeamAgent', ...siege, 'redTeamAgent']
          : ['guardrailSentinel', 'redTeamAgent', ...siege, 'redTeamAgent'];
      const mix = [...base, ...special];
      cmds.push({
        type: 'train',
        buildingId: endpoint.id,
        unit: mix[combat.length % mix.length],
      });
    }
  }

  // Apex units from an operational Training Cluster (Run): hyperscale fields
  // its Supercluster, everyone else the shared Frontier Agent.
  if (p.phase >= 2) {
    const tc = myBuildings.find((b) => b.type === 'trainingCluster' && b.buildTicks === 0);
    if (tc && tc.queue.length === 0 && p.budget > 900 && p.revenuePerSec > p.burnPerSec) {
      const apex: UnitType = p.faction === 'hyperscale' ? 'superCluster' : 'frontierAgent';
      cmds.push({ type: 'train', buildingId: tc.id, unit: apex });
    }
  }

  // defend: if something of mine got hit this tick-window, send combat units
  const attackEvent = state.events.find(
    (e) => e.type === 'underAttack' && e.player === me && e.entityId !== undefined,
  );
  if (attackEvent) {
    const victim = state.entities.find((e) => e.id === attackEvent.entityId);
    if (victim) {
      // find nearest enemy to the victim and counter-attack
      let foe: { id: number; d: number } | null = null;
      for (const e of state.entities) {
        if (e.owner === me || e.owner === -1) continue;
        if (e.kind !== 'unit') continue;
        const d = dist(entityCenter(victim), e.pos);
        if (d < 12 && (!foe || d < foe.d)) foe = { id: e.id, d };
      }
      if (foe) {
        const defenders = combat.filter((u) => u.order.kind !== 'attack').map((u) => u.id);
        if (defenders.length > 0) {
          cmds.push({ type: 'attack', unitIds: defenders, targetId: foe.id });
        }
      }
    }
  }

  // raid: enough muscle and healthy economy -> hit their nearest building
  const wantRaid = tune.raidSize[p.phase >= 2 ? 1 : 0];
  const idleCombat = combat.filter((u) => u.order.kind === 'idle');
  if (idleCombat.length >= wantRaid) {
    let target: { id: number; d: number } | null = null;
    for (const e of state.entities) {
      if (e.owner === me || e.owner === -1) continue;
      if (e.kind !== 'building') continue;
      if (!state.players[e.owner]?.alive) continue;
      const d = dist(hqC, entityCenter(e));
      if (!target || d < target.d) target = { id: e.id, d };
    }
    if (target) {
      cmds.push({
        type: 'attack',
        unitIds: idleCombat.map((u) => u.id),
        targetId: target.id,
      });
    }
  }

  // cyber offense: with an operational Adversarial Lab and a healthy economy,
  // harass the enemy's most valuable reachable building whenever an ability is
  // off cooldown (one op per think cycle).
  if (operational('adversarialLab') && p.budget > 700 && p.revenuePerSec > p.burnPerSec * 0.6) {
    let target: { id: number; score: number } | null = null;
    for (const e of state.entities) {
      if (e.owner === me || e.owner < 0 || e.kind !== 'building') continue;
      const b = e as Building;
      if (b.buildTicks !== 0) continue;
      if (!state.players[e.owner]?.alive) continue;
      const prio =
        b.type === 'inferenceEndpoint' || b.type === 'gpuCluster'
          ? 3
          : b.type === 'finopsCenter' || b.type === 'trainingCluster'
            ? 2
            : 1;
      const score = prio * 1000 - dist(hqC, entityCenter(e));
      if (!target || score > target.score) target = { id: e.id, score };
    }
    if (target) {
      // Only reach for abilities the current phase has unlocked (the heavier
      // attacks are Run-gated). Prefer the most disruptive available.
      if (
        p.phase >= CYBER.ddos.phase &&
        p.cyber.ddosCd === 0 &&
        p.budget >= CYBER.ddos.costBudget &&
        p.data >= CYBER.ddos.costData
      ) {
        cmds.push({ type: 'ddos', targetId: target.id });
      } else if (
        p.phase >= CYBER.dataPoison.phase &&
        p.cyber.poisonCd === 0 &&
        p.budget >= CYBER.dataPoison.costBudget
      ) {
        cmds.push({ type: 'dataPoison', targetId: target.id });
      } else if (
        p.phase >= CYBER.promptInjection.phase &&
        p.cyber.injectCd === 0 &&
        p.budget >= CYBER.promptInjection.costBudget &&
        p.data >= CYBER.promptInjection.costData
      ) {
        cmds.push({ type: 'promptInjection', targetId: target.id });
      }
    }
  }

  return cmds;
}
